// Constants
const DIALOG_SELECTORS = {
  imageDialog: '.tox-dialog[aria-label*="Image" i], .tox-dialog[aria-label*="Изображен" i]',
  allDialogs: '.tox-dialog',
  allTinyMCEDialogs: '.tox-dialog, .mce-window',
  tabs: '.tox-dialog__body-nav-item, [role="tab"]',
  tabSelectors: [
    '.tox-dialog__body-nav-item',
    '.tox-dialog__body-nav [role="tab"]',
    '[role="tablist"] [role="tab"]'
  ]
};

const DELAYS = {
  observerStart: [50, 100, 200, 300, 500, 800, 1200],
  tabSwitch: [50, 100, 200, 300],
  tabSwitchExtended: [100, 200, 300, 500, 800, 1200, 1800],
  dialogClose: 300,
  observerTimeout: 3000,
  observerTimeoutExtended: 5000
};

// Helper functions for TinyMCE image upload
const TinyMCEImageUploadHelpers = {
  /**
   * Get editor instance from various sources
   */
  getEditorInstance(blobInfo) {
    if (typeof tinymce !== 'undefined' && tinymce.activeEditor) {
      return tinymce.activeEditor;
    }
    return blobInfo?.editor || null;
  },

  /**
   * Extract image URL from API response
   */
  extractImageUrl(json) {
    if (json?.data?.location) {
      return json.data.location;
    }
    if (json?.location) {
      return json.location;
    }
    if (typeof json === 'string') {
      return json;
    }
    return null;
  },

  /**
   * Ensure URL is absolute
   */
  ensureAbsoluteUrl(location) {
    if (location.startsWith('http://') || location.startsWith('https://')) {
      return location;
    }
    const protocol = window.location.protocol;
    const host = window.location.host;
    return protocol + '//' + host + (location.startsWith('/') ? location : '/' + location);
  },

  /**
   * Insert image into editor and close dialogs
   */
  insertImageAndCloseDialogs(editor, location, blobInfo) {
    if (!editor) {
      return;
    }

    try {
      editor.insertContent('<img src="' + location + '" alt="' + blobInfo.filename() + '" />');
      
      // Close dialogs immediately using requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        this.closeImageDialogs(editor);
      });
    } catch (err) {
      // Silently fail
    }
  },

  /**
   * Close TinyMCE image and error dialogs
   */
  closeImageDialogs(editor) {
    try {
      const tinyMCEDialogs = document.querySelectorAll(DIALOG_SELECTORS.allTinyMCEDialogs);
      
      tinyMCEDialogs.forEach((dialog) => {
        if (this.shouldCloseDialog(dialog)) {
          this.closeDialog(dialog);
        }
      });
      
      this.clearPendingFlags(editor);
    } catch (err) {
      // Silently fail
    }
  },

  /**
   * Check if dialog should be closed
   */
  shouldCloseDialog(dialog) {
    const { isImageDialog, isErrorDialog } = this.isImageOrErrorDialog(dialog);
    return isImageDialog || isErrorDialog;
  },

  /**
   * Clear pending flags from editor
   */
  clearPendingFlags(editor) {
    if (editor._pendingImageUpload) {
      delete editor._pendingImageUpload;
    }
    if (editor._pendingImageUrl) {
      delete editor._pendingImageUrl;
    }
  },

  /**
   * Close a single dialog
   */
  closeDialog(dialog) {
    try {
      // Immediately hide dialog to prevent flickering
      dialog.style.display = 'none';
      dialog.style.visibility = 'hidden';
      dialog.style.opacity = '0';
      
      const closeBtn = this.findCloseButton(dialog);
      if (closeBtn) {
        closeBtn.click();
        return;
      }
      
      const okBtn = this.findOkButton(dialog);
      if (okBtn) {
        okBtn.click();
        return;
      }
      
      // Remove dialog from DOM as last resort
      dialog.remove();
    } catch (err) {
      // Silently fail
    }
  },

  /**
   * Find close button in dialog
   */
  findCloseButton(dialog) {
    return dialog.querySelector(
      '.tox-button[data-alloy-tabstop="true"], .mce-close, ' +
      '[aria-label*="close" i], [aria-label*="закрыть" i], ' +
      'button[title*="close" i], button[title*="Close" i]'
    );
  },

  /**
   * Find OK button in dialog
   */
  findOkButton(dialog) {
    const buttons = dialog.querySelectorAll('button');
    return Array.from(buttons).find(
      btn => btn.textContent && (btn.textContent.trim() === 'OK' || btn.textContent.trim() === 'Ok')
    );
  },

  /**
   * Check if dialog is image dialog
   */
  isImageDialog(dialog) {
    const dialogText = dialog.textContent || '';
    return dialogText.includes('Image') || 
           dialogText.includes('Изображен') ||
           dialog.querySelector('[aria-label*="image" i]') ||
           dialog.querySelector('[aria-label*="изображен" i]') ||
           dialog.classList.contains('tox-image-dialog');
  },

  /**
   * Check if dialog is error dialog
   */
  isErrorDialog(dialog) {
    const dialogText = dialog.textContent || '';
    return dialog.classList.contains('tox-alert-dialog') && 
           (dialogText.includes('src') || 
            dialogText.includes('value') || 
            dialogText.includes('Expected type'));
  },

  /**
   * Check if dialog is image or error dialog
   */
  isImageOrErrorDialog(dialog) {
    return {
      isImageDialog: this.isImageDialog(dialog),
      isErrorDialog: this.isErrorDialog(dialog)
    };
  }
};

// Dialog utilities
const DialogUtils = {
  /**
   * Find image dialog in DOM
   */
  findImageDialog() {
    return document.querySelector(DIALOG_SELECTORS.imageDialog);
  },

  /**
   * Find dialog with tabs
   */
  findDialogWithTabs() {
    const allDialogs = document.querySelectorAll(DIALOG_SELECTORS.allDialogs);
    for (const dialog of allDialogs) {
      const tabs = dialog.querySelectorAll(DIALOG_SELECTORS.tabs);
      if (tabs.length >= 3) {
        return dialog;
      }
    }
    return null;
  },

  /**
   * Find all tabs in dialog
   */
  findAllTabs(dialog = null) {
    if (!dialog) {
      dialog = this.findImageDialog() || this.findDialogWithTabs();
    }
    if (!dialog) {
      return [];
    }
    return Array.from(dialog.querySelectorAll(DIALOG_SELECTORS.tabs));
  },

  /**
   * Find tabs using multiple selectors
   */
  findTabsBySelectors() {
    // First try to find tabs within image dialog
    const imageDialog = this.findImageDialog();
    if (imageDialog) {
      const tabs = imageDialog.querySelectorAll(DIALOG_SELECTORS.tabs);
      if (tabs.length > 0) {
        return Array.from(tabs);
      }
    }

    // Then try global selectors
    for (const selector of DIALOG_SELECTORS.tabSelectors) {
      const tabs = document.querySelectorAll(selector);
      if (tabs.length > 0) {
        return Array.from(tabs);
      }
    }
    return [];
  },

  /**
   * Find upload tab
   */
  findUploadTab(allTabs) {
    if (!allTabs || allTabs.length === 0) {
      return null;
    }

    // Method 1: By position (third tab)
    if (allTabs.length >= 3) {
      return allTabs[2];
    }

    // Method 2: By text content
    const uploadTabByText = allTabs.find(tab => {
      const text = (tab.textContent || '').toLowerCase();
      return text.includes('upload') || text.includes('загрузк');
    });
    if (uploadTabByText) {
      return uploadTabByText;
    }

    // Method 3: By ID
    return allTabs.find(tab => {
      const id = tab.id || '';
      return id.includes('aria_') && (tab.textContent || '').toLowerCase().includes('upload');
    });
  },

  /**
   * Check if tab is selected
   */
  isTabSelected(tab) {
    return tab.getAttribute('aria-selected') === 'true' || 
           tab.classList.contains('tox-dialog__body-nav-item--active');
  },

  /**
   * Click tab with multiple methods
   */
  clickTab(tab) {
    tab.click();
    
    // Also try mouse events
    ['mousedown', 'mouseup', 'click'].forEach(eventType => {
      const event = new MouseEvent(eventType, {
        bubbles: true,
        cancelable: true,
        view: window
      });
      tab.dispatchEvent(event);
    });
  }
};

// Tab switching utilities
const TabSwitcher = {
  /**
   * Switch to upload tab with retries
   */
  switchToUploadTab(attempt = 1, maxAttempts = 5) {
    try {
      const allTabs = DialogUtils.findTabsBySelectors();
      
      if (allTabs.length === 0) {
        if (attempt < maxAttempts) {
          setTimeout(() => this.switchToUploadTab(attempt + 1, maxAttempts), 100 * attempt);
        }
        return;
      }

      const uploadTab = DialogUtils.findUploadTab(allTabs);
      
      if (!uploadTab) {
        if (attempt < maxAttempts) {
          setTimeout(() => this.switchToUploadTab(attempt + 1, maxAttempts), 150 * attempt);
        }
        return;
      }

      if (DialogUtils.isTabSelected(uploadTab)) {
        return;
      }

      DialogUtils.clickTab(uploadTab);

      // Verify after a delay
      setTimeout(() => {
        if (!DialogUtils.isTabSelected(uploadTab) && attempt < 3) {
          this.switchToUploadTab(attempt + 1, 3);
        }
      }, 200);
    } catch (err) {
      if (attempt < 3) {
        setTimeout(() => this.switchToUploadTab(attempt + 1, 3), 200 * attempt);
      }
    }
  },

  /**
   * Switch to upload tab in specific dialog
   */
  switchToUploadTabInDialog(imageDialog, attempt = 1, maxAttempts = 8) {
    try {
      if (!imageDialog) {
        if (attempt < maxAttempts) {
          setTimeout(() => this.switchToUploadTabInDialog(null, attempt + 1, maxAttempts), 150 * attempt);
        }
        return;
      }

      const allTabs = DialogUtils.findAllTabs(imageDialog);
      
      if (allTabs.length < 3) {
        if (attempt < maxAttempts) {
          setTimeout(() => this.switchToUploadTabInDialog(null, attempt + 1, maxAttempts), 150 * attempt);
        }
        return;
      }

      const uploadTab = allTabs[2];
      
      if (!DialogUtils.isTabSelected(uploadTab)) {
        uploadTab.click();
      }
    } catch (err) {
      if (attempt < maxAttempts) {
        setTimeout(() => this.switchToUploadTabInDialog(null, attempt + 1, maxAttempts), 200 * attempt);
      }
    }
  },

  /**
   * Start multiple switch attempts with delays
   */
  startSwitchAttempts(delays, switchFn) {
    delays.forEach(delay => {
      setTimeout(() => switchFn(), delay);
    });
  }
};

// Observer utilities
const ObserverUtils = {
  /**
   * Create observer for tab switching
   */
  createTabSwitchObserver() {
    let observerInstance = null;
    
    observerInstance = new MutationObserver((mutations) => {
      const imageDialog = DialogUtils.findImageDialog();
      if (!imageDialog) {
        return;
      }

      const allTabs = DialogUtils.findAllTabs(imageDialog);
      if (allTabs.length >= 3) {
        const uploadTab = allTabs[2];
        
        if (!DialogUtils.isTabSelected(uploadTab)) {
          uploadTab.click();
          observerInstance?.disconnect();
        } else {
          observerInstance?.disconnect();
        }
      }
    });
    
    return observerInstance;
  },

  /**
   * Start observing dialog for tabs
   */
  startObservingDialog(dialogObserver, timeout = DELAYS.observerTimeout) {
    const startObserving = () => {
      const imageDialog = DialogUtils.findImageDialog() || DialogUtils.findDialogWithTabs();
      
      if (!imageDialog) {
        return false;
      }

      const dialogBody = imageDialog.querySelector('.tox-dialog__body, .tox-dialog__body-nav') || imageDialog;
      
      dialogObserver.observe(dialogBody, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-selected', 'class']
      });
      
      dialogObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      setTimeout(() => {
        dialogObserver.disconnect();
      }, timeout);
      
      return true;
    };

    DELAYS.observerStart.forEach(delay => {
      setTimeout(() => startObserving(), delay);
    });
  }
};

// TinyMCE event handlers
const TinyMCEEventHandlers = {
  /**
   * Handle OpenDialog event
   */
  handleOpenDialog(editor, e) {
    if (e.dialog !== 'image') {
      return;
    }

    // If image is already uploaded, close dialog immediately
    if (editor._pendingImageUrl) {
      requestAnimationFrame(() => {
        const imageDialog = DialogUtils.findImageDialog();
        if (imageDialog) {
          TinyMCEImageUploadHelpers.closeDialog(imageDialog);
          TinyMCEImageUploadHelpers.clearPendingFlags(editor);
        }
      });
      return;
    }

    // Setup observer for tab switching
    const dialogObserver = ObserverUtils.createTabSwitchObserver();
    ObserverUtils.startObservingDialog(dialogObserver, DELAYS.observerTimeout);

    // Try direct tab switching with multiple attempts
    const switchToUploadTab = (attempt = 1) => {
      try {
        const allTabs = DialogUtils.findTabsBySelectors();
        
        if (allTabs.length === 0) {
          if (attempt < 5) {
            setTimeout(() => switchToUploadTab(attempt + 1), 100 * attempt);
          }
          return;
        }

        const uploadTab = DialogUtils.findUploadTab(allTabs);
        
        if (!uploadTab) {
          if (attempt < 5) {
            setTimeout(() => switchToUploadTab(attempt + 1), 150 * attempt);
          }
          return;
        }

        if (DialogUtils.isTabSelected(uploadTab)) {
          return;
        }

        DialogUtils.clickTab(uploadTab);

        // Verify after a delay
        setTimeout(() => {
          if (!DialogUtils.isTabSelected(uploadTab) && attempt < 3) {
            switchToUploadTab(attempt + 1);
          }
        }, 200);
      } catch (err) {
        if (attempt < 3) {
          setTimeout(() => switchToUploadTab(attempt + 1), 200 * attempt);
        }
      }
    };

    // Start switching attempts with multiple delays
    DELAYS.tabSwitch.forEach(delay => {
      setTimeout(() => switchToUploadTab(1), delay);
    });
  },

  /**
   * Handle pending upload - close dialogs immediately
   */
  handlePendingUpload(editor) {
    // Use requestAnimationFrame for immediate execution
    requestAnimationFrame(() => {
      try {
        const tinyMCEDialogs = document.querySelectorAll(DIALOG_SELECTORS.allTinyMCEDialogs);
        
        tinyMCEDialogs.forEach((dialog) => {
          if (TinyMCEImageUploadHelpers.shouldCloseDialog(dialog)) {
            TinyMCEImageUploadHelpers.closeDialog(dialog);
          }
        });
        
        TinyMCEImageUploadHelpers.clearPendingFlags(editor);
      } catch (err) {
        // Silently fail
      }
    });
  },

  /**
   * Handle CloseDialog event
   */
  handleCloseDialog(e) {
    // No action needed
  },

  /**
   * Handle SetContent event
   */
  handleSetContent(e) {
    // No action needed currently
  },

  /**
   * Handle ExecCommand event
   */
  handleExecCommand(e) {
    // No action needed currently
  },

  /**
   * Handle BeforeExecCommand event
   */
  handleBeforeExecCommand(editor, e) {
    const isImageCommand = e.command === 'mceImage' || 
                          e.command === 'mceInsertContent' || 
                          e.command.includes('Image');
    
    if (!isImageCommand) {
      return;
    }

    // If image upload is in progress or completed, prevent opening dialog
    if (e.command === 'mceImage' && (editor._pendingImageUpload || editor._pendingImageUrl)) {
      e.preventDefault();
      e.stopPropagation();
      
      // If image is already uploaded, it's already inserted
      if (editor._pendingImageUrl) {
        return false;
      }
    }
  },

  /**
   * Handle OpenWindow event
   */
  handleOpenWindow(editor, e) {
    // If image is already uploaded, close dialog immediately
    if (editor._pendingImageUrl) {
      requestAnimationFrame(() => {
        const imageDialog = DialogUtils.findImageDialog() || DialogUtils.findDialogWithTabs();
        if (imageDialog) {
          TinyMCEImageUploadHelpers.closeDialog(imageDialog);
          TinyMCEImageUploadHelpers.clearPendingFlags(editor);
        }
      });
      return;
    }

    // Setup observer for tab switching
    const dialogObserver = ObserverUtils.createTabSwitchObserver();
    ObserverUtils.startObservingDialog(dialogObserver, DELAYS.observerTimeoutExtended);

    // Try direct tab switching with multiple attempts
    const switchToUploadTab = (attempt = 1) => {
      try {
        // Try to find any dialog with tabs
        let imageDialog = DialogUtils.findImageDialog();
        
        // If not found, find any dialog with 3+ tabs
        if (!imageDialog) {
          imageDialog = DialogUtils.findDialogWithTabs();
        }
        
        if (!imageDialog) {
          if (attempt < 8) {
            setTimeout(() => switchToUploadTab(attempt + 1), 150 * attempt);
          }
          return;
        }

        const allTabs = DialogUtils.findAllTabs(imageDialog);
        
        if (allTabs.length < 3) {
          if (attempt < 8) {
            setTimeout(() => switchToUploadTab(attempt + 1), 150 * attempt);
          }
          return;
        }

        const uploadTab = allTabs[2];
        
        if (!DialogUtils.isTabSelected(uploadTab)) {
          uploadTab.click();
        }
      } catch (err) {
        if (attempt < 5) {
          setTimeout(() => switchToUploadTab(attempt + 1), 200 * attempt);
        }
      }
    };

    // Start switching attempts with more attempts
    DELAYS.tabSwitchExtended.forEach(delay => {
      setTimeout(() => switchToUploadTab(1), delay);
    });
  },

  /**
   * Setup MutationObserver to watch for dialogs
   */
  setupDialogObserver(editor) {
    const observer = new MutationObserver(function(mutations) {
      if (!editor._pendingImageUrl) {
        return;
      }

      // Process mutations immediately
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is a dialog
            if (node.matches && node.matches(DIALOG_SELECTORS.allTinyMCEDialogs)) {
              if (TinyMCEImageUploadHelpers.shouldCloseDialog(node)) {
                // Hide immediately before closing
                node.style.display = 'none';
                node.style.visibility = 'hidden';
                node.style.opacity = '0';
                TinyMCEImageUploadHelpers.closeDialog(node);
              }
            }
            
            // Check children for dialogs
            const dialogs = node.querySelectorAll ? node.querySelectorAll(DIALOG_SELECTORS.allTinyMCEDialogs) : [];
            dialogs.forEach((dialog) => {
              if (TinyMCEImageUploadHelpers.shouldCloseDialog(dialog)) {
                // Hide immediately before closing
                dialog.style.display = 'none';
                dialog.style.visibility = 'hidden';
                dialog.style.opacity = '0';
                TinyMCEImageUploadHelpers.closeDialog(dialog);
              }
            });
          }
        });
      });

      // Also check all existing dialogs
      requestAnimationFrame(() => {
        const tinyMCEDialogs = document.querySelectorAll(DIALOG_SELECTORS.allTinyMCEDialogs);
        tinyMCEDialogs.forEach((dialog) => {
          if (TinyMCEImageUploadHelpers.shouldCloseDialog(dialog)) {
            // Hide immediately before closing
            dialog.style.display = 'none';
            dialog.style.visibility = 'hidden';
            dialog.style.opacity = '0';
            TinyMCEImageUploadHelpers.closeDialog(dialog);
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false
    });
    
    // Check immediately if dialog already exists
    requestAnimationFrame(() => {
      const imageDialog = DialogUtils.findImageDialog();
      if (imageDialog && editor._pendingImageUrl) {
        TinyMCEImageUploadHelpers.closeDialog(imageDialog);
        observer.takeRecords();
      }
    });
  }
};

// Import Axios for HTTP requests (consistent with project approach)
import Axios from 'axios';
import { errorMessage } from '@/helper';

// Setup Axios instance with same configuration as in authMiddleware
const axiosInstance = Axios.create({
  baseURL: process.env.API_HOST,
  withCredentials: false
});

// Add authorization token interceptor (same as authMiddleware)
axiosInstance.interceptors.request.use(
  function (config) {
    if (!config.headers.common || !config.headers.common.hasOwnProperty("Authorization")) {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.common = config.headers.common || {};
        config.headers.common["Authorization"] = token;
      }
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

/**
 * Create image upload handler for TinyMCE
 */
function createImageUploadHandler() {
  return async (blobInfo, progress) => {
    const editor = TinyMCEImageUploadHelpers.getEditorInstance(blobInfo);
    
    if (editor) {
      editor._pendingImageUpload = true;
    }

    try {
      const blob = blobInfo.blob();
      const maxSizeBytes = 1 * 1024 * 1024; // 1MB
      if (blob && blob.size > maxSizeBytes) {
        // Frontend size validation to avoid hitting nginx limit
        try {
          errorMessage('File size exceeds 1MB limit.');
        } catch (e) {
          console && console.error && console.error('File size exceeds 1MB limit.');
        }

        if (editor) {
          TinyMCEImageUploadHelpers.closeImageDialogs(editor);
        }

        return '';
      }

      const formData = new FormData();
      formData.append("file", blob, blobInfo.filename());

      const response = await axiosInstance.post("/notification/upload-image", formData);

      // Extract image URL from response
      let location = TinyMCEImageUploadHelpers.extractImageUrl(response.data);
      
      if (!location || typeof location !== "string") {
        throw new Error("Invalid response format. Expected location string, got: " + JSON.stringify(response.data));
      }

      // Ensure URL is absolute
      location = TinyMCEImageUploadHelpers.ensureAbsoluteUrl(location);
      
      // Store URL for dialog update if needed
      if (editor) {
        editor._pendingImageUrl = location;
      }
      
      // Insert image directly into editor to avoid dialog issues
      TinyMCEImageUploadHelpers.insertImageAndCloseDialogs(editor, location, blobInfo);
      
      return location;
    } catch (error) {
      // Extract human-readable message
      let message = 'Failed to upload image';

      if (error.response) {
        // Backend validation / HTTP error
        message =
          error.response.data?.data ||
          error.response.data?.message ||
          `HTTP Error: ${error.response.status}`;
      } else if (error.request) {
        // Request was made but no response received
        message = 'Image upload failed due to network error';
      } else if (error.message) {
        message = error.message;
      }

      // Show error as toast, instead of TinyMCE dialog
      try {
        errorMessage(message);
      } catch (e) {
        // Fallback: swallow if helper is not available for some reason
        console && console.error && console.error(message);
      }

      // Try to immediately close any TinyMCE image/error dialogs,
      try {
        const tinyMCEDialogs = document.querySelectorAll(DIALOG_SELECTORS.allTinyMCEDialogs);
        tinyMCEDialogs.forEach(dialog => {
          if (TinyMCEImageUploadHelpers.shouldCloseDialog(dialog)) {
            TinyMCEImageUploadHelpers.closeDialog(dialog);
          }
        });
      } catch (e) {
        // Ignore DOM errors
      }

      // Clear pending flags to avoid blocking further uploads
      if (editor) {
        TinyMCEImageUploadHelpers.clearPendingFlags(editor);
      }

      return '';
    }
  };
}

function setupImageDialogHintObserver() {
  const CLASS = 'tox-image-upload-hint';
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const dialog = node.matches && node.matches('.tox-dialog')
          ? node
          : node.querySelector && node.querySelector('.tox-dialog');
        if (!dialog || dialog.querySelector('.' + CLASS)) continue;
        const footer = dialog.querySelector('.tox-dialog__footer');
        if (!footer) continue;
        const hint = document.createElement('p');
        hint.className = CLASS;
        hint.style.cssText = 'margin:0;padding:6px 16px 2px;font-size:12px;color:#909399;width:100%;';
        hint.innerHTML = 'The uploaded file must be up to 1MB in size.<br>Supported formats include jpg, jpeg, and png.';
        footer.parentNode.insertBefore(hint, footer);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

/**
 * Setup TinyMCE editor event handlers
 */
function setupTinyMCEEventHandlers(editor) {
  editor.on('OpenDialog', (e) => TinyMCEEventHandlers.handleOpenDialog(editor, e));
  editor.on('CloseDialog', (e) => TinyMCEEventHandlers.handleCloseDialog(e));
  editor.on('SetContent', (e) => TinyMCEEventHandlers.handleSetContent(e));
  editor.on('ExecCommand', (e) => TinyMCEEventHandlers.handleExecCommand(e));
  editor.on('BeforeExecCommand', (e) => TinyMCEEventHandlers.handleBeforeExecCommand(editor, e));
  editor.on('OpenWindow', (e) => TinyMCEEventHandlers.handleOpenWindow(editor, e));

  editor.on('init', () => {
    TinyMCEEventHandlers.setupDialogObserver(editor);
    setupImageDialogHintObserver();
  });
}

// Export functions for use in notifications.js
export { createImageUploadHandler, setupTinyMCEEventHandlers };
